import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { auth, ADMIN_DEMO } from '@/lib/auth';
import { BARANGAY, BRAND } from '@/data/mockData';
import { ShieldCheck, Eye, EyeOff, ArrowLeft, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const AdminLogin = () => {
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (auth.isLoggedIn()) nav('/admin', { replace: true });
  }, [nav]);

  const submit = (e) => {
    e.preventDefault();
    const res = auth.login(username, password);
    if (res.ok) {
      toast.success('Welcome back, admin!');
      nav('/admin', { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const useDemo = () => {
    setUsername(ADMIN_DEMO.username);
    setPassword(ADMIN_DEMO.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="admin-login-page">
      {/* LEFT — Branding */}
      <div className="relative hidden lg:flex flex-col justify-between bg-gradient-hero text-primary-foreground p-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        <div className="absolute -top-24 -right-12 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />

        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100" data-testid="login-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to public site
          </Link>
          <div className="mt-12 flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="logo" className="h-14 w-14 rounded-full bg-white/95 p-0.5" />
            <div>
              <div className="font-display text-xl font-bold">{BARANGAY.name}</div>
              <div className="text-xs uppercase tracking-[0.18em] opacity-75">{BARANGAY.municipality}, {BARANGAY.province}</div>
            </div>
          </div>
          <h1 className="mt-12 font-display text-4xl font-bold leading-tight max-w-md">
            Smart Barangay <span className="text-accent">Action Center</span>
          </h1>
          <p className="mt-4 text-base/relaxed opacity-85 max-w-md">
            Manage announcements, service requests, concern reports, and barangay content — all from one secure dashboard.
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-3 gap-3 max-w-md">
            <Tile label="Requests" />
            <Tile label="Concerns" />
            <Tile label="Reports" />
          </div>
        </div>
      </div>

      {/* RIGHT — Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-8 border-0 shadow-elegant">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="mt-5 font-display text-2xl font-bold">Sign in to admin portal</h2>
          <p className="mt-1 text-sm text-muted-foreground">Use your administrator credentials to continue.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="login-user">Username</Label>
              <Input
                id="login-user"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoFocus
                data-testid="login-username"
              />
            </div>
            <div>
              <Label htmlFor="login-pass">Password</Label>
              <div className="relative">
                <Input
                  id="login-pass"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" size="lg" className="w-full bg-gradient-primary text-primary-foreground" data-testid="login-submit">
              <KeyRound className="mr-2 h-4 w-4" /> Sign in
            </Button>
          </form>

          <div className="mt-6 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-primary uppercase tracking-widest">Demo Credentials</div>
                <div className="mt-1 text-sm font-mono">
                  <div>username: <strong>{ADMIN_DEMO.username}</strong></div>
                  <div>password: <strong>{ADMIN_DEMO.password}</strong></div>
                </div>
              </div>
              <Button onClick={useDemo} size="sm" variant="outline" data-testid="login-use-demo">Use demo</Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const Tile = ({ label }) => (
  <div className="rounded-xl bg-white/10 backdrop-blur p-4">
    <div className="font-display text-2xl font-bold">—</div>
    <div className="text-[11px] uppercase tracking-widest opacity-80">{label}</div>
  </div>
);

export default AdminLogin;
